import React, { useState, useEffect } from "react";
import {
  FaCheck,
  FaMapMarkerAlt,
  FaSpinner,
  FaCalendarAlt,
  FaClock,
  FaRedo,
  FaDownload,
  FaUser,
  FaEye,
  FaTimes,
  FaTag,
  FaCopy,
  FaAd,
} from "react-icons/fa";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DescriptionViewer from "../../Common/DescriptionViewer";
import AdsSubNav from '../../Common/AdsSubNav';

const NewFreeAds = () => {
  const navigate = useNavigate();
  const [freeAds, setFreeAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingAdId, setProcessingAdId] = useState(null);
  // Spinner for Approve action
  const [showApproveSpinner, setShowApproveSpinner] = useState(false);
  const [confirmAdId, setConfirmAdId] = useState(null);
  const [isConfirmChecked, setIsConfirmChecked] = useState(false);
  const [downloadAdId, setDownloadAdId] = useState(null);
  const [isDownloadChecked, setIsDownloadChecked] = useState(false);
  const [showAdImage, setShowAdImage] = useState(false);
  const [adImageUrl, setAdImageUrl] = useState("");
  // Advertisement preview modal state
  const [showAdPreview, setShowAdPreview] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);

  const stats = {
    newAds: freeAds.filter((ad) => ad.status === "approved").length,
  };

  // View advertisement function
  const viewAdvertisement = (ad) => {
    setSelectedAd(ad);
    setShowAdPreview(true);
  };

  // Close advertisement preview
  const closeAdPreview = () => {
    setShowAdPreview(false);
    setSelectedAd(null);
  };

  // Copy advertisement details
  const copyAdDetails = () => {
    if (selectedAd) {
      navigator.clipboard.writeText(selectedAd.description).then(() => {
        toast.success("Description copied to clipboard!");
      }).catch(() => {
        toast.error("Failed to copy description");
      });
    }
  };

  const fetchFreeAds = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/influencer/new/free/ad`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Response of New Free Ads:", response);
      const ads = response.data?.data || response.data;
      setFreeAds(Array.isArray(ads) ? ads : []);
      setError(null);

      // Show success message if ads are found
      if (Array.isArray(ads) && ads.length > 0) {
        console.log(`✅ Successfully loaded ${ads.length} free ads for reporter`);
      } else {
        console.log("ℹ️ No free ads available for this reporter");
      }
    } catch (err) {
      console.error("Error fetching reporter free ads:", err);
      if (err.response && err.response.status === 403) {
        toast.error(
          err.response.data?.message ||
          "You are not a verified reporter. Please apply for your ID card first."
        );
      } else if (err.response && err.response.status === 404) {
        toast.error("No free ads found for your location and verification status.");
      } else {
        setError("Failed to load free ads. Please try again.");
        toast.error("Failed to load free ads. Please check your connection.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (confirmAdId || downloadAdId || showAdImage || showAdPreview) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [confirmAdId, downloadAdId, showAdImage, showAdPreview]);

  useEffect(() => {
    fetchFreeAds();
  }, []);

  const handleAccept = async (adId) => {
    setProcessingAdId(adId);
    setShowApproveSpinner(true);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/free/ad/accepted/${adId}`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      console.log("Response of accept Free Ads:", res);
      if (res.status === 200) {
        toast.success("Free Ad accepted successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        await fetchFreeAds();
        setTimeout(() => {
          setShowApproveSpinner(false);
          navigate("/reporter/freeads/accepted");
          window.location.reload();
        }, 1500);
      } else {
        setShowApproveSpinner(false);
      }
    } catch (err) {
      setShowApproveSpinner(false);
      console.error("Error accepting free ad:", err);
      toast.error(err.response?.data?.message || "Failed to accept free ad", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setTimeout(() => {
        setProcessingAdId(null);
        setConfirmAdId(null);
        setIsConfirmChecked(false);
      }, 1500);
    }
  };

  const sortedAds = [...freeAds].sort((a, b) => {
    const dateA = new Date(a.createdAt || a.startDate).getTime();
    const dateB = new Date(b.createdAt || b.startDate).getTime();
    return dateB - dateA;
  });

  const filteredAds = sortedAds.filter((ad) => {

    return true;
  });

  // Pagination state (must be after filteredAds definition)
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(filteredAds.length / rowsPerPage);

  // Get current page data
  const paginatedAds = filteredAds.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleDownload = async (ad) => {
    try {
      const url =
        ad.mediaType?.toLowerCase() === "video" ? ad.videoUrl : ad.imageUrl;
      const fileName = `free_ad_${ad._id}.${ad.mediaType?.toLowerCase() === "video" ? "mp4" : "jpg"
        }`;

      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      toast.error("Failed to download free ad");
    } finally {
      setDownloadAdId(null);
      setIsDownloadChecked(false);
    }
  };

  const handleShowAdImage = (ad) => {
    if (ad.mediaType?.toLowerCase() === "video") {
      setAdImageUrl(ad.videoUrl);
    } else {
      setAdImageUrl(ad.imageUrl);
    }
    setShowAdImage(true);
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      {/* Spinner Overlay for Approve */}
      {showApproveSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span
                className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin"
                style={{ borderWidth: "4px" }}
              />
              {/* Middle ring */}
              <span
                className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slow"
                style={{ borderWidth: "4px", animationDuration: "2s" }}
              />
              {/* Inner ring */}
              <span
                className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slower"
                style={{ borderWidth: "4px", animationDuration: "3s" }}
              />
            </div>
            <span className="text-lg font-semibold text-blue-700 tracking-wide">
              Processing Approval...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the status.
            </span>
          </div>
        </div>
      )}
      {/* Modern Header - Compact for Mobile */}
      <div className="max-w-7xl mx-auto mb-3 sm:mb-6 px-2 sm:px-4 mt-2 sm:mt-6">
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-xl sm:rounded-2xl shadow-xl p-2 sm:p-6 text-white relative overflow-hidden">
          {/* Decorative background element */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />

          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-[9px] font-black uppercase tracking-tight border border-white/20 transition-all whitespace-nowrap shadow-sm"
              >
                ← Back
              </button>
              <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                <div className="p-2 sm:p-3 bg-white/20 rounded-lg sm:rounded-xl backdrop-blur-md border border-white/30 hidden xs:flex">
                  <FaAd className="text-sm sm:text-2xl text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-sm sm:text-2xl md:text-3xl font-black tracking-tight truncate">New Reward Ads</h1>
                  <p className="text-blue-100/80 text-[9px] sm:text-sm font-bold uppercase tracking-wider mt-0.5 truncate">Browse tasks & earn rewards</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <div className="bg-white/20 backdrop-blur-md rounded-lg sm:rounded-xl p-2 sm:px-4 sm:py-3 border border-white/30 text-center flex-1 sm:flex-none min-w-[70px] sm:min-w-[100px] shadow-lg">
                <div className="text-sm sm:text-2xl font-black">{stats.newAds || 0}</div>
                <div className="text-[8px] sm:text-xs font-black uppercase tracking-widest text-blue-100">Live</div>
              </div>
              <button
                className="p-2.5 sm:p-4 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl transition-all border border-white/30 backdrop-blur-md text-white shadow-lg active:scale-95 group flex items-center justify-center"
                onClick={fetchFreeAds}
                disabled={loading}
              >
                <FaRedo className={`w-3 h-3 sm:w-4 sm:h-4 ${loading ? "animate-spin" : "group-hover:rotate-180 transition-transform duration-500"}`} />
              </button>
            </div>
          </div>
        </div>
        <div className="mt-2 hidden xs:block">
          <p className="text-[9px] sm:text-xs text-slate-500 font-bold uppercase tracking-widest flex items-center gap-2 px-1">
            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
            Targeted ads based on your location and verified profile status
          </p>
        </div>
      </div>

      <AdsSubNav type="free" />

      {loading && (
        <div className="flex justify-center items-center py-12">
          <FaSpinner className="animate-spin text-blue-500 text-4xl" />
        </div>
      )
      }

      {
        error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            <p>{error}</p>
          </div>
        )
      }

      {/* Free Advertisement Cards Grid */}
      {
        !loading && !error && (
          <div className="max-w-7xl mx-auto px-1 sm:px-4 md:px-6 lg:px-8">
            {filteredAds.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-6">
                {paginatedAds.map((ad, index) => (
                  <motion.div
                    key={ad._id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2, delay: index * 0.03 }}
                    className="bg-white rounded-[1.5rem] border border-slate-100 shadow-xl shadow-slate-200/40 overflow-hidden group hover:border-indigo-200 transition-all flex flex-row md:flex-col"
                  >
                    {/* Left Side (Mobile Image) / Top (Desktop Image) */}
                    <div className="relative w-28 sm:w-32 md:w-full md:h-48 overflow-hidden bg-slate-900 flex-shrink-0 border-r md:border-r-0">
                      {ad.mediaType && ad.mediaType.toLowerCase() === "video" ? (
                        <video
                          src={ad.videoUrl}
                          className="w-full h-full object-cover opacity-80"
                        />
                      ) : (
                        <img
                          src={ad.imageUrl}
                          alt="ad"
                          className="w-full h-full object-cover opacity-80 group-hover:scale-110 transition-transform duration-700"
                        />
                      )}

                      {/* Free Badge Overlay */}
                      <div className="absolute top-2 left-2 md:hidden">
                        <span className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase text-green-700 bg-green-50/90 backdrop-blur-sm border border-green-200 shadow-sm">
                          Free
                        </span>
                      </div>

                      {/* Floating Play/View Icon */}
                      <button
                        onClick={() => handleShowAdImage(ad)}
                        className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-md rounded-full border border-white/20 text-white">
                          <FaEye />
                        </div>
                      </button>
                    </div>

                    {/* Right Side (Mobile Details) / Bottom (Desktop Details) */}
                    <div className="flex-1 p-3 sm:p-5 flex flex-col justify-between min-w-0">
                      <div className="space-y-2">
                        <div className="flex justify-between items-start gap-2">
                          <div className="min-w-0">
                            <h3 className="text-slate-900 font-black text-xs sm:text-base tracking-tight truncate leading-tight">
                              {ad.adType}
                            </h3>
                            <p className="text-slate-400 text-[8px] font-bold uppercase tracking-wider font-mono truncate">
                              ID: {ad._id.slice(-8)}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0 hidden md:block">
                            <div className="px-2 py-0.5 rounded text-[10px] font-black uppercase text-green-700 bg-green-50 border border-green-200">
                              Free
                            </div>
                          </div>
                        </div>

                        {/* Compact Stats Grid */}
                        <div className="grid grid-cols-1 gap-1.5 py-1.5 border-y border-slate-50">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <div className="p-1 bg-indigo-50 rounded-md text-indigo-600 text-[9px] flex-shrink-0">
                              <FaMapMarkerAlt />
                            </div>
                            <span className="text-slate-600 text-[10px] font-bold truncate">
                              {ad.state && String(ad.state).trim() !== "" ? (Array.isArray(ad.state) ? ad.state.filter(Boolean).join(", ") : ad.state) : "Global"}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 min-w-0">
                            <div className="p-1 bg-amber-50 rounded-md text-amber-600 text-[9px] flex-shrink-0">
                              <FaCalendarAlt />
                            </div>
                            <span className="text-slate-600 text-[10px] font-black truncate">
                              {new Date(ad.createdAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          </div>
                        </div>

                        <div className="group/desc relative cursor-pointer" onClick={() => viewAdvertisement(ad)}>
                          <div className="bg-slate-50 px-2 py-1.5 rounded-lg text-slate-500 text-[10px] font-bold italic line-clamp-1 pr-6 group-hover/desc:bg-indigo-50 transition-colors">
                            "{ad.description}"
                          </div>
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-indigo-400 group-hover/desc:text-indigo-600 transition-colors">
                            <FaEye size={12} />
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-3">
                        <button
                          onClick={() => setConfirmAdId(ad._id)}
                          disabled={processingAdId === ad._id}
                          className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-slate-800 hover:to-slate-900 text-white py-2 rounded-xl font-black text-[9px] uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 shadow-md shadow-emerald-500/20"
                        >
                          {processingAdId === ad._id ? (
                            <FaSpinner className="animate-spin" />
                          ) : (
                            <>
                              <FaCheck size={10} /> Accept Free Ad
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Expiration Glow Bar */}
                    <div className="absolute top-0 right-0 w-12 h-12 bg-emerald-500/5 blur-xl group-hover:bg-emerald-500/10 pointer-events-none" />
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-[3rem] border border-dashed border-slate-200">
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaTag className="text-slate-300 text-3xl" />
                </div>
                <h3 className="text-slate-900 font-black text-xl tracking-tight">No Free Ads Found</h3>
                <p className="text-slate-400 text-sm font-bold mt-1">Check back later for new opportunities.</p>
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-4 sm:mt-6 md:mt-8 px-2 sm:px-4 pb-6">
                <nav className="inline-flex items-center space-x-1 sm:space-x-2 bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-200 p-1.5 sm:p-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-all duration-200 ${currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 hover:border-blue-200'
                      }`}
                  >
                    <span className="flex items-center gap-1 sm:gap-2">
                      <FaUser className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Previous</span><span className="sm:hidden">Prev</span>
                    </span>
                  </button>

                  <div className="flex items-center space-x-0.5 sm:space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = i + 1;
                      const isActive = pageNum === currentPage;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-all duration-200 ${isActive
                            ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md'
                            : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 hover:border-blue-200'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-all duration-200 ${currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 hover:border-blue-200'
                      }`}
                  >
                    <span className="flex items-center gap-1 sm:gap-2">
                      <span className="hidden sm:inline">Next</span><span className="sm:hidden">Next</span>
                      <FaUser className="w-3 h-3 sm:w-4 sm:h-4" />
                    </span>
                  </button>
                </nav>
              </div>
            )}
          </div>
        )
      }

      {/* Advertisement Preview Modal */}
      {
        showAdPreview && selectedAd && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 md:p-6 overflow-y-auto bg-slate-900/40 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden relative"
            >
              {/* Modal Header */}
              <div className="bg-slate-900 px-6 py-6 sm:px-10 flex justify-between items-center text-white">
                <div>
                  <h2 className="text-xl font-[1000] tracking-tight">Free Ad Intelligence</h2>
                  <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">Detailed View</p>
                </div>
                <button onClick={closeAdPreview} className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl transition-all">
                  <FaTimes size={20} />
                </button>
              </div>

              <div className="p-6 sm:p-10 max-h-[70vh] overflow-y-auto custom-scrollbar">
                {/* Media Section */}
                <div className="rounded-[2rem] overflow-hidden bg-slate-900 mb-8 shadow-inner">
                  {selectedAd.mediaType?.toLowerCase() === "video" ? (
                    <video src={selectedAd.videoUrl} controls className="w-full max-h-[300px] mx-auto" />
                  ) : (
                    <img src={selectedAd.imageUrl} className="w-full h-auto object-contain max-h-[400px]" alt="Ad" />
                  )}
                </div>

                {/* BIG SIZE DESCRIPTION */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-slate-900 font-[1000] text-lg tracking-tight">Campaign Brief</h3>
                    <button onClick={copyAdDetails} className="flex items-center gap-2 text-indigo-600 font-black text-[10px] uppercase tracking-widest hover:text-indigo-800">
                      <FaCopy /> Copy Text
                    </button>
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-[2rem] p-6 sm:p-8">
                    <div className="text-slate-600 text-base sm:text-lg md:text-xl leading-relaxed font-bold italic">
                      <DescriptionViewer
                        description={selectedAd.description || ""}
                        textSize="text-base sm:text-lg md:text-xl"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )
      }

      {/* Download Confirmation Modal */}
      {
        downloadAdId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2 sm:p-4">
            <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl p-4 sm:p-6 md:p-8 w-full max-w-md mx-4 text-center relative">
              <h2 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-3 md:mb-4 text-gray-800">
                Confirm Download
              </h2>
              <p className="mb-3 sm:mb-4 md:mb-6 text-xs sm:text-sm md:text-base text-gray-600">
                Do you want to download this advertisement?
              </p>

              <div className="flex items-center justify-center mb-3 sm:mb-4 md:mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isDownloadChecked}
                    onChange={(e) => setIsDownloadChecked(e.target.checked)}
                    className="mr-2 sm:mr-3 h-3 w-3 sm:h-4 sm:w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-xs sm:text-sm text-gray-700">
                    I confirm this download
                  </span>
                </label>
              </div>

              <div className="flex justify-center gap-2 sm:gap-3 md:gap-4">
                <button
                  onClick={() => {
                    const ad = filteredAds.find((ad) => ad._id === downloadAdId);
                    handleDownload(ad);
                  }}
                  className={`px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 rounded text-xs sm:text-sm md:text-base font-semibold transition ${isDownloadChecked
                    ? "bg-blue-500 text-white hover:bg-blue-600"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  disabled={!isDownloadChecked}
                >
                  <span className="hidden sm:inline">Yes, Download</span><span className="sm:hidden">Download</span>
                </button>
                <button
                  onClick={() => {
                    setDownloadAdId(null);
                    setIsDownloadChecked(false);
                  }}
                  className="px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-semibold transition text-xs sm:text-sm md:text-base"
                >
                  Cancel
                </button>
              </div>
              <button
                className="absolute top-1.5 sm:top-2 right-1.5 sm:right-2 text-gray-400 hover:text-gray-600 text-xl sm:text-2xl"
                onClick={() => {
                  setDownloadAdId(null);
                  setIsDownloadChecked(false);
                }}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
          </div>
        )
      }

      {/* Accept Confirmation Modal */}
      {
        confirmAdId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2 sm:p-4">
            <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl p-4 sm:p-6 md:p-8 w-full max-w-md mx-4 text-center relative">
              <h2 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-3 md:mb-4 text-gray-800">
                Confirm Acceptance
              </h2>
              <p className="mb-3 sm:mb-4 md:mb-6 text-xs sm:text-sm md:text-base text-gray-600">
                Are you sure you want to accept this free advertisement?
              </p>

              <div className="flex items-center justify-center mb-3 sm:mb-4 md:mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isConfirmChecked}
                    onChange={(e) => setIsConfirmChecked(e.target.checked)}
                    className="mr-2 sm:mr-3 h-3 w-3 sm:h-4 sm:w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <span className="text-xs sm:text-sm text-gray-700">
                    I understand and confirm this acceptance
                  </span>
                </label>
              </div>

              <div className="flex justify-center gap-2 sm:gap-3 md:gap-4">
                <button
                  onClick={() => {
                    handleAccept(confirmAdId);
                    const ad = filteredAds.find((ad) => ad._id === confirmAdId);
                    handleDownload(ad);
                  }}
                  className={`px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 rounded text-xs sm:text-sm md:text-base font-semibold transition ${isConfirmChecked
                    ? "bg-green-500 text-white hover:bg-green-600"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  disabled={processingAdId === confirmAdId || !isConfirmChecked}
                >
                  {processingAdId === confirmAdId
                    ? "Processing..."
                    : <span><span className="hidden sm:inline">Yes, </span>Accept</span>}
                </button>
                <button
                  onClick={() => {
                    setConfirmAdId(null);
                    setIsConfirmChecked(false);
                  }}
                  className="px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-semibold transition text-xs sm:text-sm md:text-base"
                  disabled={processingAdId === confirmAdId}
                >
                  Cancel
                </button>
              </div>
              <button
                className="absolute top-1.5 sm:top-2 right-1.5 sm:right-2 text-gray-400 hover:text-gray-600 text-xl sm:text-2xl"
                onClick={() => {
                  setConfirmAdId(null);
                  setIsConfirmChecked(false);
                }}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
          </div>
        )
      }

      {/* Ad Image/Video Popup */}
      {
        showAdImage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 relative w-full max-w-lg flex flex-col items-center">
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-xl sm:text-2xl p-2"
                onClick={() => setShowAdImage(false)}
                aria-label="Close"
              >
                &times;
              </button>
              <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">Free Ad Preview</h2>
              <div className="w-full flex justify-center bg-slate-100 rounded overflow-hidden">
                {adImageUrl ? (
                  adImageUrl.match(/\.(mp4|webm|ogg)$/i) ? (
                    <video
                      src={adImageUrl}
                      controls
                      className="max-h-[60vh] max-w-full"
                      autoPlay
                    />
                  ) : (
                    <img
                      src={adImageUrl}
                      alt="Ad"
                      className="max-h-[60vh] max-w-full object-contain"
                    />
                  )
                ) : (
                  <div className="text-gray-500 py-10">No media available</div>
                )}
              </div>
            </div>
          </div>
        )
      }
    </>
  );
};

export default NewFreeAds;
